import CheckCard from 'components/CheckCard'
import React, { useEffect, useRef, useState } from 'react'
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native'
import { Button, Text, TextInput } from 'react-native-paper'
import { useDispatch, useSelector } from 'react-redux'

import { clampQuantity, getRecipes } from 'src/recipes'

// Feels like a bunch of these could be grouped up, but this is close enough for now.
// The order these are listed in is the order they are displayed in
const CATEGORIES = {
  VEGETABLES: 'Vegetables',
  FRUIT_AND_VEG: 'Fruit and Vegetables',
  HERBS_AND_SPICES: 'Herbs and Spices',
  BAKING: 'Baking',
  GRAINS_AND_CEREALS: 'Grains and Cereals',
  CANNED_GOODS: 'Canned Goods',
  PANTRY_ESSENTIALS: 'Pantry Essentials',
  PASTA_AND_RICE: 'Pasta and Rice',
  SAUCES: 'Sauces',
  BAKERY: 'Bakery',
  NUTS_SEEDS_AND_DRIED_FRUITS: 'Nuts, Seeds, and Dried Fruits',
  DAIRY: 'Dairy',
  FROZEN_GOODS: 'Frozen Goods',
  MEAT: 'Meat',
  PROTEIN: 'Protein',
  MISC: 'Miscellaneous',
}

const collapseIngredients = (recipes, usedServings) => {
  const ingredients = recipes.flatMap(({ servings, extendedIngredients }) =>
    extendedIngredients.map((ingredient) => ({
      ...ingredient,
      quantity: (ingredient.quantity / servings) * usedServings,
    })),
  )

  let object = {}

  ingredients.forEach((ingredient) => {
    const { category, name, quantity, unit } = ingredient

    if (!object[category]) object[category] = {}

    const key = `${name}|${unit}`

    if (!object[category][key]) object[category][key] = 0

    object[category][key] += clampQuantity(quantity)
  })

  return object
}

const userItemStyles = StyleSheet.create({
  form: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    marginBottom: 15,
  },
  input: { flexGrow: 1 },
  inputOutline: { borderRadius: 15 },
})

const UserItems = ({ onFormSelected }) => {
  const [value, setValue] = useState('')
  const dispatch = useDispatch()
  const items = useSelector(({ userItems: { items } }) => items)

  const onPress = () => {
    dispatch({ type: 'ADD_ITEM', item: value })

    setValue('')
    onFormSelected()
  }

  return (
    <View style={{ flex: 1, width: '100%' }}>
      <Text style={styles.sectionTitle}>User Items List</Text>

      {Object.entries(items).map(([title, checked], index) => (
        <CheckCard
          {...{
            key: `user-item-${index}`,
            title,
            checked,
            onPress: () => dispatch({ type: 'TOGGLE_ITEM', item: title }),
            onLongPress: () => dispatch({ type: 'REMOVE_ITEM', item: title }),
          }}
        />
      ))}

      <View style={userItemStyles.form}>
        <TextInput
          placeholder="Custom item..."
          value={value}
          onChangeText={setValue}
          mode={'outlined'}
          dense={true}
          style={userItemStyles.input}
          outlineStyle={userItemStyles.inputOutline}
          onPressIn={onFormSelected}
        />
        <Button appearance="ghost" onPress={onPress}>
          Add
        </Button>
      </View>
    </View>
  )
}

const Ingredient = ({ name, quantity }) => {
  const dispatch = useDispatch()
  const checked = useSelector(
    ({
      recipes: {
        shoppingList: { [name]: checked },
      },
    }) => checked,
  )
  const [title, unit] = name.split('|')

  return (
    <CheckCard
      {...{
        title,
        checked,
        onPress: () =>
          dispatch({
            type: 'TOGGLE_INGREDIENT',
            ingredient: name,
          }),
        subtitle: `${quantity} ${unit}`,
      }}
    />
  )
}

const Category = ({ ingredients, name }) => {
  if (!ingredients) return null

  return (
    <React.Fragment>
      <Text style={styles.sectionTitle}>{name}</Text>

      {Object.entries(ingredients).map(([key, quantity]) => (
        <Ingredient
          {...{
            key,
            name: key,
            quantity,
          }}
        />
      ))}
    </React.Fragment>
  )
}

const ShopScreen = () => {
  const keys = useSelector(({ recipes: { keys } }) => keys)
  const { meals, servings } = useSelector(({ settings }) => settings)
  const [groupedIngredients, setGroupedIngredients] = useState({})
  const scrollRef = useRef()

  useEffect(() => {
    setGroupedIngredients(
      collapseIngredients(getRecipes(keys, meals), servings),
    )
  }, [keys, meals, servings])

  const onFormSelected = () => {
    // This is a bit hacky, but is allows a bit of time for the keyboard to pop up before scrolling down
    setTimeout(() => {
      scrollRef.current.scrollToEnd({ animated: true })
    }, 200)
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : null}
      style={{
        flex: 1,
        paddingTop: Platform.OS == 'ios' ? 50 : 10,
        backgroundColor: '#132936',
      }}
    >
      <ScrollView
        ref={scrollRef}
        keyboardShouldPersistTaps={'always'}
        style={styles.scrollContainer}
        showsHorizontalScrollIndicator={false}
      >
        <Text style={styles.heading}>
          Shopping list for {meals} meals with {servings} servings
        </Text>

        {Object.entries(CATEGORIES).map(([category, name]) => (
          <Category
            key={category}
            name={name}
            ingredients={groupedIngredients[category]}
          />
        ))}

        <UserItems {...{ onFormSelected }} />
      </ScrollView>
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexGrow: 1,
  },
  scrollContainer: {
    padding: 5,
    width: '100%',
    margin: 'auto',
    maxWidth: Platform.OS === 'web' ? '700px' : null,
  },
  mainContainer: {
    flex: 1,
    height: '100%',
    justifyContent: 'space-between',
  },
  title: {
    margin: 10,
  },
  heading: {
    marginVertical: 10,
    marginHorizontal: 5,
    fontWeight: 'bold',
    fontSize: 18,
  },
  sectionTitle: {
    marginTop: 25,
    marginBottom: 5,
    marginLeft: 7,
    fontWeight: 'bold',
  },
  userList: {
    flex: 1,
    flexShrink: 1,
    marginBottom: 10,
  },
  ListFooterComponent: {
    marginBottom: 10,
  },
})

export default ShopScreen
